'use client';

import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useTranslations } from 'next-intl';
import { CustomImage as Image } from '..';
import gsap from 'gsap';
import styles from './TwoImageInfoSection.module.css';

export const TwoImageInfoSection = ({
  className,
}: {
  className?: string;
}) => {
  const t = useTranslations('common');
  const container = useRef<HTMLDivElement | null>(null);
  const image1 = useRef<HTMLImageElement | null>(null);
  const image2 = useRef<HTMLImageElement | null>(null);

  const gsapContext = useGsapContext();

  const animInit = () => {
    if (!window || !image1.current || !image2.current) return;

    gsapContext.add(() => {
      const tl = gsap.timeline({
        defaults: {
          duration: 1,
        },
        scrollTrigger: {
          trigger: container.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0,
          invalidateOnRefresh: true,
        },
      });

      tl.fromTo(
        image1.current,
        {
          y: 200,
          scale: 1,
        },
        {
          y: -100,
          scale: 1.2,
        },
      );

      tl.fromTo(
        image2.current,
        {
          y: 200,
          scale: 2.5,
        },
        {
          y: -100,
          scale: 1.5,
        },
        '-=1',
      );
    });
  };

  useIsomorphicLayoutEffect(() => {
    const timer = setTimeout(() => {
      animInit();
    }, 100);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <section
      className={className ? `${styles.root} ${className}` : styles.root}
      ref={container}
    >
      <div className={styles.content}>
        <div className={styles.imgInfoElement}>
          <div className={styles.imgContainer}>
            <Image
              ref={image1}
              src="web_v2/illustrations/air-front-tilt.png/w_1920"
              fill
              alt={t('twoImageInfoSection.image.alt.uhRingAir')}
            />
          </div>
          <p>
            <b>{t('twoImageInfoSection.content.gmsLight')}</b>{' '}
            {t('twoImageInfoSection.text.11xLighterTypical')}
          </p>
        </div>
        <div className={styles.imgInfoElement}>
          <div className={styles.imgContainer}>
            <Image
              ref={image2}
              src="web_v2/illustrations/air-full-tilt.png/w_1920"
              fill
              alt={t('twoImageInfoSection.image.alt.uhRingAir')}
            />
          </div>
          <p>
            <b>{t('twoImageInfoSection.content.mmSleek')}</b>{' '}
            {t('twoImageInfoSection.text.discreetDesignEveryday')}
          </p>
        </div>
      </div>
    </section>
  );
};
